import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import PermissionToast from './PermissionToast';
import { 
  HomeIcon, 
  ChartBarIcon,
  CogIcon,
  BellIcon,
  ArrowRightOnRectangleIcon,
  KeyIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { getUserPermissions, PERMISSIONS } from '../utils/permissionUtils';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = getAuth();
  const [userPermissions, setUserPermissions] = useState({});
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const [permissionToast, setPermissionToast] = useState({ show: false, message: '' });
  const [currentUser, setCurrentUser] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  useEffect(() => {
    const loadUserPermissions = async () => {
      try {
        const currentUserStr = localStorage.getItem('currentUser');
        console.log('Sidebar - Raw localStorage currentUser:', currentUserStr);
        
        const currentUser = JSON.parse(currentUserStr || '{}');
        const userId = currentUser.uid || currentUser.id || currentUser.firebaseUid;
        
        console.log('Sidebar - Current user from localStorage:', currentUser);
        console.log('Sidebar - User ID being used:', userId);
        console.log('Sidebar - Available user properties:', Object.keys(currentUser));
        console.log('Sidebar - All localStorage keys:', Object.keys(localStorage));
        
        // Set current user for display
        setCurrentUser(currentUser);
        
        if (userId) {
          const permissions = await getUserPermissions(userId);
          console.log('Sidebar - Loaded permissions:', permissions);
          setUserPermissions(permissions);
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
      } finally {
        setLoadingPermissions(false);
      }
    };

    loadUserPermissions();
  }, []);

  const hasPermission = (permission) => {
    if (!permission) return true;
    
    // If permissions are still loading, deny access
    if (loadingPermissions) {
      console.log('Sidebar - Permissions still loading, denying access');
      return false;
    }
    
    // If no permissions loaded, deny access
    if (Object.keys(userPermissions).length === 0) {
      console.log('Sidebar - No permissions loaded, denying access');
      return false;
    }
    
    console.log('Sidebar - Checking permission:', permission);
    console.log('Sidebar - User permissions:', userPermissions);
    
    const parts = permission.split('.');
    if (parts.length === 2) {
      const [category, perm] = parts;
      const result = userPermissions[category]?.[perm] === true;
      console.log('Sidebar - Permission result:', result, 'category:', category, 'perm:', perm);
      return result;
    }
    
    return false;
  };




  
  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('currentUser');
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  
  const menuItems = [
    { 
      name: 'Dashboard', 
      icon: HomeIcon, 
      path: '/',
      permission: PERMISSIONS.SIDEBAR_MENU_DASHBOARD
    },
    { 
      name: 'User Management', 
      icon: CogIcon, 
      path: '/user-management',
      permission: PERMISSIONS.SIDEBAR_MENU_USER_MANAGEMENT
    },
    { 
      name: 'Analytics', 
      icon: ChartBarIcon, 
      path: '/analytics',
      permission: PERMISSIONS.SIDEBAR_MENU_ANALYTICS
    },
    { 
      name: 'Settings', 
      icon: CogIcon, 
      path: '/settings',
      permission: PERMISSIONS.SIDEBAR_MENU_SETTINGS
    },
    { 
      name: 'Notifications', 
      icon: BellIcon, 
      path: '/notifications',
      permission: PERMISSIONS.SIDEBAR_MENU_NOTIFICATIONS
    },
  ];

  return (
    <div className={`${isCollapsed ? 'w-16' : 'w-64'} bg-gray-900 text-white flex flex-col fixed left-0 top-0 h-full z-50 flex-shrink-0 transition-all duration-300 ease-in-out`}>
      {/* Header with Toggle Button */}
      <div className="p-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <div className="flex items-center">
              <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center mr-3">
                <span className="text-white font-bold text-sm">K</span>
              </div>
              <h1 className="text-xl font-bold">Kerblet</h1>
            </div>
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-2 rounded-lg hover:bg-gray-800 transition-colors"
            title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          >
            {isCollapsed ? (
              <Bars3Icon className="h-5 w-5 text-gray-300" />
            ) : (
              <XMarkIcon className="h-5 w-5 text-gray-300" />
            )}
          </button>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          {(() => {
            console.log('Sidebar - All menu items:', menuItems);
            console.log('Sidebar - Filtered menu items:', menuItems.filter(item => hasPermission(item.permission)));
            return menuItems.filter(item => hasPermission(item.permission)).map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <li key={item.name}>
                  <button
                    className={`sidebar-item w-full text-left ${isActive ? 'active' : ''} ${isCollapsed ? 'justify-center' : ''}`}
                    onClick={() => {
                      if (hasPermission(item.permission)) {
                        navigate(item.path);
                      } else {
                        console.log(`Permission denied for sidebar item: ${item.name}`);
                        setPermissionToast({ 
                          show: true, 
                          message: `You don't have permission to access ${item.name}` 
                        });
                      }
                    }}
                    title={isCollapsed ? item.name : ''}
                  >
                    <item.icon className="w-5 h-5" />
                    {!isCollapsed && <span className="font-medium ml-3">{item.name}</span>}
                  </button>
                </li>
              );
            });
          })()}
        </ul>
      </nav>

             {/* User Profile */}
       <div className="p-4 border-t border-gray-800">
         {!isCollapsed ? (
           <div className="text-center">
             {/* User Avatar and ID */}
             <div className="flex flex-col items-center mb-4">
               <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center mb-2">
                 <span className="text-gray-300 text-lg font-semibold">
                   {currentUser?.displayName ? currentUser.displayName.charAt(0).toUpperCase() : 
                    currentUser?.email ? currentUser.email.charAt(0).toUpperCase() : 'A'}
                 </span>
               </div>
               <div className="text-center">
                 <p className="text-xs text-gray-400 break-all">
                   {currentUser?.displayName || currentUser?.email || (loadingPermissions ? 'Loading...' : 'Admin User')}
                 </p>
               </div>
             </div>
             
             {/* Action Buttons */}
             <div className="space-y-2">
               <button
                 onClick={() => navigate('/change-password')}
                 className="sidebar-item w-full text-center text-blue-400 hover:text-blue-300"
                 title="Change Password"
               >
                 <KeyIcon className="w-4 h-4 inline mr-2" />
                 <span className="text-xs font-medium">Change Password</span>
               </button>
               <button
                 onClick={handleLogout}
                 className="sidebar-item w-full text-center text-red-400 hover:text-red-300"
                 title="Sign Out"
               >
                 <ArrowRightOnRectangleIcon className="w-4 h-4 inline mr-2" />
                 <span className="text-xs font-medium">Sign Out</span>
               </button>
             </div>
           </div>
         ) : (
           <div className="text-center">
             {/* Collapsed User Avatar */}
             <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
               <span className="text-gray-300 text-sm font-semibold">
                 {currentUser?.displayName ? currentUser.displayName.charAt(0).toUpperCase() : 
                  currentUser?.email ? currentUser.email.charAt(0).toUpperCase() : 'A'}
               </span>
             </div>
             
             {/* Collapsed Action Buttons */}
             <div className="space-y-2">
               <button
                 onClick={() => navigate('/change-password')}
                 className="sidebar-item w-full justify-center text-blue-400 hover:text-blue-300"
                 title="Change Password"
               >
                 <KeyIcon className="w-4 h-4" />
               </button>
               <button
                 onClick={handleLogout}
                 className="sidebar-item w-full justify-center text-red-400 hover:text-red-300"
                 title="Sign Out"
               >
                 <ArrowRightOnRectangleIcon className="w-4 h-4" />
               </button>
             </div>
           </div>
         )}
       </div>
      
      <PermissionToast 
        message={permissionToast.message}
        isVisible={permissionToast.show}
        onClose={() => setPermissionToast({ show: false, message: '' })}
      />
    </div>
  );
};

export default Sidebar; 